import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/customer-ui/components/dialog";
import { OptionContent } from "@babackapp/customer-ui/components/option";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import { cn } from "@babackapp/customer-ui/lib/utils";
import { Loader2Icon, XIcon, ZoomInIcon } from "lucide-react";
import {
  type ComponentProps,
  type SyntheticEvent,
  Suspense,
  useState,
} from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import {
  type OptionChoice,
  useVariantSelector,
} from "../../hooks/use-variant-selector";
import { shopifyThumbnail } from "@babackapp/return-variables-registry";

/** Twice the rendered `size-8`, for dense screens. */
const ITEM_IMAGE_WIDTH = 64;

/** The zoom library sizes both of its layers to their content by default. */
const FILL = { width: "100%", height: "100%" };

/**
 * Photo of the zoom dialog. Mounted with the dialog content, so the gesture
 * hint shows again on every opening, until the first zoom.
 */
function ZoomableImage({ src, alt }: { src: string; alt: string }) {
  const t = useCustomText();
  const [zoomed, setZoomed] = useState(false);
  // Touch screens pinch; mice and trackpads scroll or double-click.
  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia?.("(pointer: coarse)").matches;

  return (
    <>
      <TransformWrapper
        minScale={1}
        maxScale={5}
        centerOnInit
        doubleClick={{ mode: "toggle", step: 1.5 }}
        onTransform={(_, { scale }) => {
          if (scale > 1) setZoomed(true);
        }}
      >
        <TransformComponent
          wrapperStyle={FILL}
          contentStyle={FILL}
          // The library turns pointer events off on the image itself.
          wrapperClass="cursor-grab active:cursor-grabbing"
        >
          <img src={src} alt={alt} className="size-full object-contain" />
        </TransformComponent>
      </TransformWrapper>
      <p
        aria-live="polite"
        className={cn(
          "pointer-events-none absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-background/90 px-3 py-1.5 text-sm text-foreground shadow-md transition-opacity duration-300",
          zoomed && "opacity-0",
        )}
      >
        <ZoomInIcon className="size-4" aria-hidden />
        {t(
          // Picked through a ternary, out of reach of the key scanner:
          // @widget-key: exchangeOptions.variant.zoomHintTouch common
          // @widget-key: exchangeOptions.variant.zoomHintPointer common
          isTouch
            ? "exchangeOptions.variant.zoomHintTouch"
            : "exchangeOptions.variant.zoomHintPointer",
        )}
      </p>
    </>
  );
}

type VariantFooterProps = {
  orderId: string;
  lineItemId: string;
  shopId: string;
  onVariantChange: (variantId: string | null) => void;
  /** Variant already in the draft, kept when the customer comes back. */
  exchangeVariantId: string | null;
};

/**
 * Thumbnail of the selected variant. The spinner mounts only while loading and fades in past a short delay
 * (`starting:` runs the transition on mount too), so cached images don't flash it.
 * Rendered as the dialog trigger, so closing the zoom gives the focus back.
 * The corner badge tells it opens a larger photo.
 */
function VariantThumbnail({
  src,
  ...buttonProps
}: { src: string | undefined } & ComponentProps<"button">) {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isLoading = loadedSrc !== src;

  if (!src) return null;

  const settle = (e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.currentSrc === e.currentTarget.src) setLoadedSrc(src);
  };

  return (
    <button
      type="button"
      {...buttonProps}
      className="relative size-12 shrink-0 cursor-zoom-in overflow-hidden rounded-md bg-muted outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <img
        src={src}
        alt=""
        // A load or error queued for the previous image can run after `src`
        // changed: only the request for the current `src` ends the spinner.
        onLoad={settle}
        onError={settle}
        className="size-full object-cover"
      />
      <span
        aria-hidden
        className="absolute bottom-0.5 right-0.5 flex size-4 items-center justify-center rounded-sm bg-background/85 text-foreground shadow-sm"
      >
        <ZoomInIcon className="size-3" />
      </span>
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center bg-muted/70 opacity-100 transition-opacity delay-150 starting:opacity-0">
          <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
        </span>
      )}
    </button>
  );
}

type ChoiceSelectProps = {
  value: string;
  choices: (OptionChoice & { label?: string })[];
  locked: boolean;
  withImages: boolean;
  onChange: (value: string) => void;
};

/**
 * One select of the picker. With `withImages`, each item shows the image of
 * the variant it leads to: unlike a hover preview, it works on touch screens.
 *
 * A locked select stays focusable (`aria-disabled`, never opens) instead of
 * `disabled`: picking the last reachable value locks it in the same commit,
 * and Radix would hand the focus back to a disabled trigger, i.e. to `<body>`.
 * A select locked while open (shop refetch) is closed by the `open` prop alone,
 * which Radix does not report through `onOpenChange`: the open state is reset
 * here so it does not reopen by itself once unlocked.
 */
function ChoiceSelect({
  value,
  choices,
  locked,
  withImages,
  onChange,
}: ChoiceSelectProps) {
  const t = useCustomText();
  const [isOpen, setIsOpen] = useState(false);
  if (isOpen && locked) setIsOpen(false);
  const open = isOpen && !locked;
  return (
    <Select
      value={value}
      // Inside a form, Radix syncs a hidden native select that reports "" when
      // the value is not among its previous options (product switch): ignored.
      onValueChange={(v) => {
        if (v) onChange(v);
      }}
      open={open}
      onOpenChange={(next) => setIsOpen(next && !locked)}
    >
      <SelectTrigger
        aria-disabled={locked || undefined}
        className={cn("w-auto", locked && "cursor-not-allowed opacity-50")}
      >
        <SelectValue />
      </SelectTrigger>
      {/* Item-aligned (the default) lines the selected ItemText up with the
          trigger: an image before it would shift the list off to the left. */}
      <SelectContent position={withImages ? "popper" : "item-aligned"}>
        {choices.map((c) => (
          <SelectItem
            key={c.value}
            value={c.value}
            disabled={!c.available}
            leading={
              withImages &&
              (c.imageUrl ? (
                <img
                  src={shopifyThumbnail(c.imageUrl, ITEM_IMAGE_WIDTH)}
                  alt=""
                  loading="lazy"
                  className="size-8 shrink-0 rounded bg-muted object-cover"
                />
              ) : (
                <div className="size-8 shrink-0 rounded bg-muted" />
              ))
            }
          >
            {c.label ?? c.value}
            {!c.available && ` (${t("exchangeOptions.variant.unavailable")})`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function VariantFooterInner({
  orderId,
  lineItemId,
  shopId,
  onVariantChange,
  exchangeVariantId,
}: VariantFooterProps) {
  const {
    productOption,
    selectedProductId,
    selectedOptions,
    selectedProduct,
    selectedImageUrl,
    selectableOptions,
    hasRealChoice,
    handleProductChange,
    handleOptionChange,
  } = useVariantSelector(
    orderId,
    lineItemId,
    shopId,
    onVariantChange,
    exchangeVariantId,
  );
  const t = useCustomText();

  if (!hasRealChoice) return null;

  const productTitle = selectedProduct?.productTitle ?? "";

  return (
    <OptionContent className="flex-wrap items-center">
      <Dialog>
        <DialogTrigger asChild>
          <VariantThumbnail
            // Full size, like the zoom: opening it then hits the cache.
            src={selectedImageUrl}
            aria-label={t("exchangeOptions.variant.zoomImage", {
              product: productTitle,
            })}
          />
        </DialogTrigger>
        {/* Near full screen, so the photo can be zoomed. Tapping the photo
            no longer closes it (it would end every pan): a close button
            stays over the photo, with the overlay and Escape. */}
        <DialogContent
          className="h-[calc(100dvh-1rem)] max-h-none w-[calc(100%-1rem)] max-w-3xl gap-0 overflow-hidden p-0"
          hideCloseButton
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{productTitle}</DialogTitle>
          {selectedImageUrl && (
            <ZoomableImage src={selectedImageUrl} alt={productTitle} />
          )}
          <DialogClose
            aria-label={t("common.close")}
            className="absolute right-3 top-3 flex size-9 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <XIcon className="size-5" />
          </DialogClose>
        </DialogContent>
      </Dialog>
      {productOption && (
        <ChoiceSelect
          value={selectedProductId}
          choices={productOption.choices}
          locked={productOption.locked}
          withImages={productOption.withImages}
          onChange={handleProductChange}
        />
      )}
      {selectableOptions.map((opt) => (
        <ChoiceSelect
          // Remounted per product: a select reused across products would
          // carry the previous product's values (see onValueChange above).
          key={`${selectedProductId}:${opt.name}`}
          value={selectedOptions[opt.name] ?? ""}
          choices={opt.choices}
          locked={opt.locked}
          withImages={opt.withImages}
          onChange={(v) => handleOptionChange(opt.name, v)}
        />
      ))}
    </OptionContent>
  );
}

export function VariantFooter(props: VariantFooterProps) {
  return (
    <Suspense fallback={null}>
      <VariantFooterInner {...props} />
    </Suspense>
  );
}
